/**
 * Novanalyse — auth shell (the premium two-panel chrome for the LAB identity).
 *
 * The canvas shared by login / forgot / reset:
 *   • LEFT — the navy brand canvas (NovaMark wordmark "novanalyse · laboratoire" · eyebrow + tagline
 *     + positioning · a cross-fading capability line · a counts-only KPI strip · the four-pillar
 *     ecosystem strip · mono copyright). Navy in EVERY theme; hidden below 900px (a compact wordmark
 *     header replaces it on the form panel).
 *   • RIGHT — the theme toggle, the page's form (children), and the secure three-row footer.
 *
 * Auth wiring is untouched (D1) — this is CHROME + UX only. FR-default, full AR/RTL, WCAG 2.2 AA
 * (focus-visible ring, labelled controls, aria-pressed toggle, role=contentinfo footer).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/app/i18n";
import { useThemeStore, THEMES, type Theme } from "@/design/themeStore";
import { NovaMark } from "./brand";
import { EcosystemStrip } from "./EcosystemStrip";
import { BRAND } from "./brand.config";
import "./auth.css";

const ROTATE_MS = 3200;
const FADE_MS = 240;

/** Wordmark lockup (white text, cyan accent word). */
function Wordmark({ size, className }: { size: number; className?: string }) {
  return (
    <div className={`nv-auth__word${className ? ` ${className}` : ""}`}>
      <NovaMark size={size} />
      <span className="nv-auth__wm">
        {BRAND.wordmark}
        <span>{BRAND.wordmarkAccent}</span>
      </span>
    </div>
  );
}

/** Cross-fading capability line (~3.2s cycle, 240ms fade). Respects prefers-reduced-motion. */
function CapabilityRotator() {
  const lines = BRAND.capabilityLines;
  const [i, setI] = useState(0);
  const [out, setOut] = useState(false);

  useEffect(() => {
    if (lines.length <= 1) return;
    let fadeTimer: ReturnType<typeof setTimeout>;
    const cycle = setInterval(() => {
      setOut(true);
      fadeTimer = setTimeout(() => {
        setI((v) => (v + 1) % lines.length);
        setOut(false);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => {
      clearInterval(cycle);
      clearTimeout(fadeTimer);
    };
  }, [lines.length]);

  return (
    <p className="nv-auth__rotator" aria-live="polite">
      <span className="nv-auth__chk" aria-hidden>
        <CheckCircle />
      </span>
      <span className={`nv-auth__line${out ? " is-out" : ""}`}>{lines[i]}</span>
    </p>
  );
}

const fmtCount = (n: number | null): string =>
  n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k` : String(n);

interface PublicStats {
  laboratoires?: number | null;
  analyses?: number | null;
}

/** Counts-only public KPI strip — silently degrades to "—" if the endpoint is unreachable. */
function PublicStatStrip() {
  const [stats, setStats] = useState<PublicStats>({ laboratoires: null, analyses: null });

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    fetch("/api/v1/public/stats", { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { data?: PublicStats } | null) => {
        if (alive && body?.data) setStats(body.data);
      })
      .catch(() => {
        /* silent — the strip shows "—" */
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  return (
    <dl className="nv-auth__kpis">
      <div className="nv-auth__kpi">
        <dd className="nv-auth__kpi-v nv-mono">{fmtCount(stats.laboratoires ?? null)}</dd>
        <dt className="nv-auth__kpi-k">Laboratoires</dt>
      </div>
      <div className="nv-auth__kpi">
        <dd className="nv-auth__kpi-v nv-mono">{fmtCount(stats.analyses ?? null)}</dd>
        <dt className="nv-auth__kpi-k">Analyses</dt>
      </div>
      <div className="nv-auth__kpi">
        <dd className="nv-auth__kpi-v nv-mono">99.9%</dd>
        <dt className="nv-auth__kpi-k">Disponibilité</dt>
      </div>
    </dl>
  );
}

const THEME_LABEL_KEY: Record<Theme, string> = {
  pro: "theme.pro",
  claire: "theme.claire",
  dark: "theme.dark",
};

function ThemeIcon({ theme }: { theme: Theme }) {
  if (theme === "pro") return <Sparkle />;
  if (theme === "claire") return <Sun />;
  return <Moon />;
}

function ThemeToggle() {
  const { t } = useT();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  return (
    <div className="nv-theme-pick" role="group" aria-label={t("theme.group")}>
      {THEMES.map((opt) => {
        const label = t(THEME_LABEL_KEY[opt]);
        return (
          <button
            key={opt}
            type="button"
            className={opt === theme ? "is-on" : ""}
            onClick={() => setTheme(opt)}
            aria-pressed={opt === theme}
            title={label}
          >
            <span aria-hidden>
              <ThemeIcon theme={opt} />
            </span>
            <span className="nv-sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function AuthFooter() {
  return (
    <footer className="nv-auth__foot" role="contentinfo">
      <div className="nv-auth__foot-row nv-auth__foot-row--dev">
        <span>Développé par</span>
        <NovaMark size={16} radius={5} />
        <span>{BRAND.legalName}</span>
        <span className="nv-auth__sep" aria-hidden />
        <span className="nv-auth__url nv-mono">{BRAND.legalUrl}</span>
      </div>
      <div className="nv-auth__foot-row">
        <span>Conforme Loi 18-07</span>
        <span className="nv-auth__sep" aria-hidden />
        <span className="nv-auth__url nv-mono">{BRAND.copyright}</span>
      </div>
    </footer>
  );
}

export interface AuthShellProps {
  children: ReactNode;
}

/** The two-panel auth chrome. Pass the page form as {children}. */
export function AuthShell({ children }: AuthShellProps) {
  // Pin a single rotator/strip instance per mount (no remount churn on theme/locale change).
  const idRef = useRef(0);
  idRef.current ||= 1;

  return (
    <div className="nv-auth">
      {/* LEFT — brand canvas (navy in every theme) */}
      <aside className="nv-auth__canvas">
        <div className="nv-auth__grid" aria-hidden />
        {/* watermark inherits white via .nv-auth__watermark { color:#fff } (currentColor) */}
        <NovaMark size={360} tone="glyph" className="nv-auth__watermark" aria-hidden />

        <Wordmark size={34} />

        <div className="nv-auth__pitch">
          <p className="nv-auth__eyebrow">{BRAND.eyebrow}</p>
          <h1 className="nv-auth__tagline">{BRAND.tagline}</h1>
          <p className="nv-auth__positioning">{BRAND.positioning}</p>
          <p className="nv-auth__serif">{BRAND.accentSerif}</p>
          <CapabilityRotator />
          <PublicStatStrip />
        </div>

        <div className="nv-auth__bottom">
          <EcosystemStrip />
          <p className="nv-auth__copy nv-mono">{BRAND.copyright}</p>
        </div>
      </aside>

      {/* RIGHT — form panel */}
      <section className="nv-auth__panel">
        <div className="nv-auth__panel-top">
          <ThemeToggle />
        </div>
        <div className="nv-auth__panel-body">
          <div className="nv-auth__card">
            <Link to="/login" className="nv-auth__mobile-word">
              <NovaMark size={30} />
              <span className="nv-auth__wm">
                {BRAND.wordmark}
                <span>{BRAND.wordmarkAccent}</span>
              </span>
            </Link>
            {children}
            <AuthFooter />
          </div>
        </div>
      </section>
    </div>
  );
}

/* ── inline icons (no icon-kit dependency; all currentColor, aria-hidden by parent) ── */
const ICON = {
  width: 15,
  height: 15,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function Sparkle() {
  return (
    <svg {...ICON}>
      <path d="M12 3l1.8 4.7L18.5 9.5 13.8 11.3 12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" />
      <path d="M19 14l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7z" />
    </svg>
  );
}
function Sun() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}
function Moon() {
  return (
    <svg {...ICON}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}
function CheckCircle() {
  return (
    <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.5l2.5 2.5 4.5-5" />
    </svg>
  );
}

export default AuthShell;
